import { Accessor, createEffect, createMemo, createSignal, Signal } from 'solid-js';
import { createEventListener } from '@solid-primitives/event-listener';

import { swipeArea } from '../utils';
import { useIsActive } from './Active';

/** 自动补帧器 */
export function createInterpolator(threshold: number, onUpdate: (value: number) => void) {
  let value = 0;
  let target = 0;
  let frameCount = 0;
  let totalFrames = 0;
  let isAnimating = false;
  let diff = 0;

  function setValue(newValue: number) {
    isAnimating = false;
    if (value === newValue) return;
    value = newValue;
    target = newValue;
    onUpdate(value);
  }

  function animateTo(newTarget: number) {
    if (target === newTarget) return;
    diff = newTarget - value;
    const diffAbs = Math.abs(diff);
    if (diffAbs < threshold) {
      value = newTarget;
      onUpdate(value);
      return;
    }

    target = newTarget;
    totalFrames = Math.ceil(diffAbs / threshold);
    frameCount = 0;
    if (!isAnimating) {
      isAnimating = true;
      requestAnimationFrame(update);
    }
  }

  function update() {
    if (!isAnimating) return;
    frameCount++;
    value = value + diff / totalFrames;

    if (frameCount < totalFrames) {
      requestAnimationFrame(update);
    } else {
      isAnimating = false;
      value = target;
    }
    onUpdate(value);
  }

  return { setValue, animateTo };
}

export function createBottomSheetDrag(
  element: Accessor<HTMLDivElement | undefined>,
  position: Signal<number>,
  onFinish: (isClose?: boolean) => void
) {
  const [drag, setDrag] = createSignal(false);
  createEffect(() => {
    const el = element();
    if (!el) return;

    let start = 0;
    // const [last, setLast] = position;
    let height = 0;
    const last = createMemo(() => (1 - position[0]()) * height);
    function setLast(value: number) {
      return position[1](1 - value / height);
    }

    // function upPos(value: number) {
    //   el!.style.transform = `translate3d(0, ${(value * 100) / height}%, 0)`;
    // }
    // const itp = createInterpolator(50, upPos);
    /** 手机上拖动事件触发很慢，导致会明显掉帧，这里进行平衡补帧 */

    /**
     * 像皮筋公式 https://gist.github.com/originell/6961057
     * (1.0 - (1.0 / ((x * c / d) + 1.0))) * d;
     * 以下数据根据ios滚动回弹测试得出的弹性系数常量为0.54
     * 100 0.8 80
     * 200 0.68 136
     * 300 0.59 177
     * 400 0.52 208
     * 500 0.47 235
     * 600 0.42 252
     * 700 0.38 266
     * 800 0.35 280
     * @returns
     */
    function rubberband(x: number) {
      return (1.0 - 1.0 / ((x * 0.54) / height + 1.0)) * height;
    }

    function updateDrag(move: number) {
      let currentY = start + move;
      if (currentY < 0) {
        currentY = -rubberband(-currentY);
      }
      setLast(currentY);
    }

    let isAne = false;
    function finishDrag(v?: number) {
      if (isAne) return;
      isAne = true;
      if (v !== undefined) {
        onFinish(v > 0.3 || last() > height / 2);
      } else if (last() !== 0) {
        onFinish();
      }
    }

    // 滚动元素
    let scrollEl: HTMLElement | null;
    let scrollTop = 0;
    let enableDrag: boolean = false;
    function isScroll(el: Element) {
      return el.scrollHeight - el.clientHeight > 1;
    }
    function disableEvent(event: TouchEvent) {
      event.stopPropagation();
      event.preventDefault();
    }
    createTouchEvent(el, {
      onDragStart(move, event) {
        setDrag(true);
        scrollEl = event.target as any;
        scrollTop = 0;
        enableDrag = true;
        while (scrollEl) {
          if (isScroll(scrollEl)) {
            scrollTop = scrollEl.scrollTop;
            enableDrag = scrollTop > -16;
            break;
          }
          if (scrollEl === el) {
            scrollEl = null;
            break;
          }
          scrollEl = scrollEl.parentElement;
        }
      },
      onDrag(move, event) {
        let isDrag = true;
        if (scrollEl) {
          // 仅在顶部生效
          const scrollTop = scrollEl.scrollTop;
          isDrag = enableDrag && scrollTop <= 16;
          const isUp = scrollEl.scrollHeight - scrollTop - scrollEl.clientHeight > scrollTop;
          scrollEl.style.overscrollBehaviorY = isUp && isDrag ? 'none' : 'contain';
        } else {
          disableEvent(event);
        }
        if (enableDrag) {
          const moveTop = move.y - scrollTop;
          if (moveTop > 0) {
            disableEvent(event);
            updateDrag(moveTop);
          }
        }
      },
      onDragEnd({ vy, y }) {
        setDrag(false);
        if (scrollEl) {
          scrollEl.style.overscrollBehaviorY = 'contain';
        }
        if (enableDrag && y - scrollTop > 0) {
          finishDrag(vy);
        }
      },
    });

    let enableDrag2: boolean | undefined;
    createTouchEvent(document as any, {
      onDragStart() {
        enableDrag2 = undefined;
        height = el.clientHeight;
        start = last();
        isAne = false;
        // 停止动画
        setLast(start);
      },
      onDrag(move, event) {
        if (enableDrag2 === undefined) {
          // 在swip back区域水平滑动
          enableDrag2 = move.begin.clientX <= swipeArea && Math.abs(move.x) > Math.abs(move.y);
        }
        if (enableDrag2) {
          updateDrag(move.x);
          disableEvent(event);
        }
      },
      onDragEnd({ vx }) {
        finishDrag(enableDrag2 ? vx : undefined);
      },
      options: {
        start: { capture: true },
        move: { capture: true },
      },
    });
  });

  return drag;
}

function createTouchEvent(
  el: HTMLElement,
  {
    onDragStart,
    onDrag,
    onDragEnd,
    options,
  }: {
    onDragStart?(move: MoveData, event: TouchEvent): void;
    onDrag?(move: MoveData, event: TouchEvent): void;
    onDragEnd?(move: MoveData, event: TouchEvent): void;
    options?: {
      start?: AddEventListenerOptions;
      move?: AddEventListenerOptions;
      end?: AddEventListenerOptions;
    };
  }
) {
  let startTouch: Touch | undefined;
  const move: MoveData = {
    x: 0,
    vx: 0,
    y: 0,
    vy: 0,
    time: 0,
    begin: undefined as any,
    prev: undefined as any,
  };

  function touchStart(event: TouchEvent) {
    const {
      touches: [touch],
      timeStamp,
    } = event;
    startTouch = move.begin = move.prev = touch;
    move.x = move.y = move.vx = move.vy = 0;

    move.time = timeStamp;
    onDragStart?.(move, event);
  }

  // 计算指数加权平均速度
  function calcVelocity(x: number, pv: number, time: number) {
    const nv = x / time;
    return nv * 0.8 + pv * 0.2;
  }

  function touchMove(event: TouchEvent) {
    const {
      touches: [touch],
      timeStamp,
    } = event;
    if (!startTouch) return;
    const timeDelta = timeStamp - move.time; // 单位为毫秒

    // 计算速度
    move.vx = calcVelocity(touch.clientX - move.prev.clientX, move.vx, timeDelta);
    move.vy = calcVelocity(touch.clientY - move.prev.clientY, move.vy, timeDelta);
    move.time = timeStamp;
    move.x = touch.clientX - startTouch.clientX;
    move.y = touch.clientY - startTouch.clientY;

    move.prev = touch;
    onDrag?.(move, event);
  }

  function touchEnd(event: TouchEvent) {
    startTouch = undefined;
    onDragEnd?.(move, event);
  }

  const active = useIsActive();
  const target = createMemo(() => (active() ? el : undefined));

  createEventListener(target, 'touchstart', touchStart, {
    passive: false,
    ...options?.start,
  });

  createEventListener(target, 'touchmove', touchMove, {
    passive: false,
    ...options?.move,
  });
  createEventListener(target, 'touchcancel', touchEnd, options?.end);
  createEventListener(target, 'touchend', touchEnd, options?.end);
}

interface MoveData {
  begin: Touch;
  prev: Touch;
  x: number;
  vx: number;
  y: number;
  vy: number;
  time: number;
}
